import { useState } from 'react'

import { Button } from '@krp/ui-kit'

import { EQUIPMENT_CATEGORIES } from '../model/categories'
import { agentCard, driverRows, isNewerVersion, supportInfo, type DriverRowVm } from '../model/drivers'
import { linkPhase } from '@krp/client-runtime'
import type { EquipmentSnapshot, HardwareActions, HardwareLinks, HardwareStatus } from '../model/types'
import { Mark, type MarkTone } from './Mark'

const AGENT_TONE: Record<string, MarkTone> = { installed: 'ok', missing: 'block', unknown: 'wait' }

function DriverRow({ row, links, actions, status }: { row: DriverRowVm; links: HardwareLinks; actions: HardwareActions; status: HardwareStatus }) {
  const link = links.drivers[row.id] ?? null
  const atol = row.id === 'atol-kkt'
  return (
    <li className={`hw-driver hw-box hw-box--${row.tone}`} aria-label={row.title}>
      <p>
        <strong>{row.title}</strong> <Mark glyph={row.glyph} word={row.word} tone={row.tone} />
        {row.version !== null && <span className="hw-hint"> · версия {row.version}</span>}
      </p>
      {row.text.length > 0 && <p className="hw-hint">{row.text}</p>}
      {row.action === 'download' && link === null && <p className="hw-hint">Ссылка не задана — файл выдаёт администратор.</p>}
      {(row.action === 'download' || row.action === 'check') && (
        <div className="hw-buttons">
          {row.action === 'download' &&
            link !== null &&
            (atol ? (
              <Button variant="primary" actionId="platform.hardware.download-driver" onClick={() => actions.downloadDriver(row.id)}>
                Скачать драйвер АТОЛ
              </Button>
            ) : (
              <Button variant="primary" actionId="platform.hardware.terminal-driver-download" onClick={() => actions.downloadDriver(row.id)}>
                Скачать драйвер
              </Button>
            ))}
          <Button
            variant="secondary"
            actionId="platform.hardware.driver-check"
            disabled={status.checking}
            disabledReason={status.checking ? 'Идёт проверка' : null}
            onClick={actions.checkDrivers}
          >
            Проверить
          </Button>
        </div>
      )}
    </li>
  )
}

/**
 * Вкладка «Драйверы» (п. 2.2, 3.1, 3.2): что надо поставить на этот компьютер. Карточка агента, карточки драйверов,
 * свёрнутые «Другое оборудование — появится» и «Сведения для поддержки».
 */
export function DriversTab({
  snapshot,
  links,
  status,
  actions,
}: {
  snapshot: EquipmentSnapshot
  links: HardwareLinks
  status: HardwareStatus
  actions: HardwareActions
}) {
  const [planned, setPlanned] = useState(false)
  const [support, setSupport] = useState(false)
  const phase = linkPhase(snapshot.link, snapshot.now)
  const card = agentCard(snapshot)
  const rows = driverRows(snapshot)
  const choose = phase === 'choose'
  const word = choose ? 'Выберите программу' : card.word
  const glyph = choose ? '!' : card.glyph
  const tone: MarkTone = choose ? 'warn' : (AGENT_TONE[card.state] ?? 'wait')
  const newer = card.state === 'installed' && isNewerVersion(links.recommendedAgentVersion, card.version)
  const otherEquipment = EQUIPMENT_CATEGORIES.filter((item) => !item.supported)

  return (
    <section aria-label="Драйверы" className="hw-tab">
      <article className={`hw-driver hw-box hw-box--${tone}`} aria-label="Программа-драйвер Kalmykov" data-stale={card.stale || undefined}>
        <p>
          <strong>Программа-драйвер Kalmykov</strong> <Mark glyph={glyph} word={word} tone={tone} />
        </p>
        {card.state === 'installed' && (
          <p className="hw-hint">
            версия {card.version}
            {card.port !== null && <> · порт {card.port}</>}
            {card.modeWord !== null && <> · {card.modeWord}</>}
            {card.startedAt !== null && <> · запущена {card.startedAt}</>}
          </p>
        )}
        {newer && <p className="hw-hint">Есть новая версия: {links.recommendedAgentVersion}.</p>}
        <div className="hw-buttons">
          {card.state === 'missing' &&
            !choose &&
            (links.agentInstaller !== null ? (
              <Button variant="primary" actionId="platform.hardware.download-agent" onClick={actions.downloadAgent}>
                Скачать программу-драйвер
              </Button>
            ) : (
              <span className="hw-hint">Ссылка не задана — файл выдаёт администратор.</span>
            ))}
          {newer && links.agentInstaller !== null && (
            <Button variant="secondary" actionId="platform.hardware.update-agent" onClick={actions.downloadAgent}>
              Скачать новую версию
            </Button>
          )}
        </div>
      </article>

      <ul className="hw-list" aria-label="Драйверы оборудования">
        {rows.map((row) => (
          <DriverRow key={row.id} row={row} links={links} actions={actions} status={status} />
        ))}
      </ul>

      <div className="hw-buttons">
        <Button variant="ghost" actionId="platform.hardware.toggle" aria-expanded={planned} onClick={() => setPlanned((value) => !value)}>
          Другое оборудование — появится
        </Button>
      </div>
      {planned && (
        <div>
          <p className="hw-hint">
            Названия — как в 1С («Подключаемое оборудование»). Поддержка этих видов оборудования появится: здесь они будут настраиваться так же, как касса и
            терминалы.
          </p>
          <ul className="hw-planned" aria-label="Оборудование, поддержка которого появится">
            {otherEquipment.map((item) => (
              <li key={item.id} className="hw-planned__item">
                {item.title} <span className="hw-hint">Появится</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="hw-buttons">
        <Button variant="ghost" actionId="platform.hardware.toggle" aria-expanded={support} onClick={() => setSupport((value) => !value)}>
          Сведения для поддержки
        </Button>
      </div>
      {support && (
        <div>
          <pre className="hw-support" aria-label="Сведения для поддержки">
            {supportInfo(snapshot)}
          </pre>
          <Button variant="secondary" actionId="platform.hardware.copy-diagnostics" onClick={actions.copyDiagnostics}>
            Скопировать сведения
          </Button>
        </div>
      )}
    </section>
  )
}
